import { useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { SkillSourceBadge } from "@/components/skills/skill-source-badge";
import { useDeleteSkill, useSkills } from "@/lib/api/hooks";
import type { ApiSkillSummary } from "@/lib/api/client";

export const Route = createFileRoute("/_authenticated/skills")({
  component: SkillsPage,
  head: () => ({ meta: [{ title: "Skills — CSaaS" }] }),
});

function SkillsPage() {
  const { data: skills = [], isLoading, error } = useSkills();
  const [pendingDelete, setPendingDelete] = useState<ApiSkillSummary | null>(null);
  const deleteSkill = useDeleteSkill();

  const confirmDelete = () => {
    if (!pendingDelete) return;
    deleteSkill.mutate(pendingDelete.name, {
      onSuccess: () => {
        toast.success("Skill deleted", { description: pendingDelete.name });
        setPendingDelete(null);
      },
      onError: (err) => {
        toast.error("Delete failed", { description: err.message });
      },
    });
  };

  return (
    <div className="max-w-7xl space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl font-semibold tracking-tight">Skills</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Skills available in the catalog — git-mounted, user-uploaded, and org-shared bundles.
          </p>
        </div>
        <Button asChild>
          <Link to="/skills/import">
            <Plus className="h-4 w-4" /> Import skill
          </Link>
        </Button>
      </div>

      {error && (
        <div className="rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive">
          {error.message}
        </div>
      )}

      {isLoading && skills.length === 0 && (
        <p className="text-sm text-muted-foreground">Loading skills…</p>
      )}

      {!isLoading && skills.length === 0 && !error && (
        <p className="text-sm text-muted-foreground">No skills registered yet.</p>
      )}

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {skills.map((s) => {
          const canDelete = s.source !== "git";
          return (
            <Card
              key={s.name}
              className="group h-full transition-all hover:border-primary/40 hover:shadow-sm"
            >
              <CardContent className="space-y-3 pt-6">
                <div className="flex items-start justify-between gap-2">
                  <Link to="/skills/$name" params={{ name: s.name }} className="min-w-0 flex-1">
                    <div className="font-display font-semibold transition-colors group-hover:text-primary">
                      {s.name}
                    </div>
                    {s.path && (
                      <code className="break-all font-mono text-[11px] text-muted-foreground">
                        {s.path}
                      </code>
                    )}
                  </Link>
                  {canDelete && (
                    <button
                      type="button"
                      aria-label={`Delete ${s.name}`}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setPendingDelete(s);
                      }}
                      className="rounded p-1 text-muted-foreground opacity-0 transition-opacity hover:bg-destructive/10 hover:text-destructive group-hover:opacity-100 focus:opacity-100"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  )}
                </div>
                <Link to="/skills/$name" params={{ name: s.name }} className="block space-y-3">
                  {s.description && (
                    <p className="line-clamp-4 text-sm text-muted-foreground">{s.description}</p>
                  )}
                  <div className="flex items-center gap-1.5">
                    <SkillSourceBadge source={s.source} visibility={s.visibility} />
                  </div>
                </Link>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <AlertDialog
        open={pendingDelete !== null}
        onOpenChange={(open) => !open && setPendingDelete(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this skill?</AlertDialogTitle>
            <AlertDialogDescription>
              Removes <span className="font-mono">{pendingDelete?.name}</span> from the catalog.
              Jobs already running against it will continue; new jobs will fail to find it. This is
              a soft delete — the underlying record is retained for audit.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteSkill.isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              disabled={deleteSkill.isPending}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteSkill.isPending ? "Deleting…" : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
